import { createAction, createAsyncThunk, createEntityAdapter, createSelector, createSlice } from '@reduxjs/toolkit';
import { Auth0DecodedHash } from 'auth0-js';

import { requestStatus, captureError } from '@clatter/platform';
import jwtDecode from 'jwt-decode';
import { baseApi } from '../services/baseApi';
import { usersApi } from '../services';

interface ImpersonateState {
  loadingStatus:
    | typeof requestStatus.initial
    | typeof requestStatus.error
    | typeof requestStatus.pending
    | typeof requestStatus.fulfilled;
  impersonated: Auth0DecodedHash | null;
  error?: null | string;
}

export const NG_IMPERSONATE_FEATURE_KEY = 'ng_impersonate';

const adapter = createEntityAdapter();

export const stopImpersonating = createAsyncThunk('auth/stopImpersonating', async (_, { dispatch }) => {
  try {
    // clear impersonate first so RTK will not include X-Clatter-Impersonate token
    await dispatch(clearImpersonated());

    // then reset all RTK Query cache to clear impersonated user data
    await dispatch(baseApi.util.resetApiState());
    await dispatch(usersApi.util.resetApiState());
  } catch (error) {
    captureError(error, { message: 'Stop Impersonating error', component: 'impersonate.slice' });
    throw error;
  }
});

export const clearImpersonated = createAction('CLEAR_IMPERSONATED');
export const setImpersonatedUser = createAction<Auth0DecodedHash>('SET_IMPERSONATED');

export const initialState = adapter.getInitialState<ImpersonateState>({
  loadingStatus: requestStatus.pending,
  impersonated: null,
  error: null,
});

//@ts-ignore
export const impersonateSlice = createSlice({
  name: NG_IMPERSONATE_FEATURE_KEY,
  initialState: initialState,
  extraReducers: (builder) => {
    builder
      .addCase(setImpersonatedUser, (state, { payload }) => {
        if (!payload.idTokenPayload) {
          return;
        }

        let decodedToken: any;

        try {
          decodedToken = jwtDecode(payload.accessToken!);
        } catch (e) {
          captureError(e, { message: 'Invalid impersonated access token', component: 'impersonate.slice' });
          state.impersonated = payload;
        }

        //NOTE: it was told that this will not change...
        const impersonatedRoles: string[] = decodedToken['https://clatter.io/roles'] || [];

        const impersonatedWithRoles = {
          ...payload,
          idTokenPayload: {
            ...payload?.idTokenPayload,
            roles: impersonatedRoles,
          },
        };

        state.impersonated = impersonatedWithRoles;
      })
      .addCase(clearImpersonated, (state) => {
        state.impersonated = null;
      })
      .addCase(stopImpersonating.pending, (state) => {
        state.impersonated = null;
        state.loadingStatus = requestStatus.pending;
      })
      .addCase(stopImpersonating.fulfilled, (state) => {
        state.loadingStatus = requestStatus.fulfilled;
      })
      .addCase(stopImpersonating.rejected, (state) => {
        state.loadingStatus = requestStatus.error;
      });
  },
});

export const impersonateReducer = impersonateSlice.reducer;

const {} = adapter.getSelectors();

export const getImperosnatedState = (rootState: any) => rootState[NG_IMPERSONATE_FEATURE_KEY];

export const impersonateSelectors = {
  getImpersonatedUser: createSelector(getImperosnatedState, ({ impersonated }) => impersonated?.idTokenPayload),
  loadingStatus: createSelector(getImperosnatedState, ({ loadingStatus }) => loadingStatus),
};
